import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';

import { safeSessionStorage } from '@/lib/safe-storage';

type PrV2ReplyDraftsByThread = Record<string, string>;
type PrV2ReplyDraftsByPr = Record<string, PrV2ReplyDraftsByThread>;
type PrV2ActiveReplyTargetsByThread = Record<string, string>;
type PrV2ActiveReplyTargetsByPr = Record<string, PrV2ActiveReplyTargetsByThread>;

type PrV2ReplyDraftStore = {
  /**
   * Reply draft text scoped by PR key first, then stable thread draft key.
   * This keeps PR tabs isolated while preserving per-thread drafts.
   */
  replyDraftsByPr: PrV2ReplyDraftsByPr;
  /**
   * Active reply target scoped by PR key first, then stable thread draft key.
   * This preserves which comment's reply composer was open across remounts.
   */
  activeReplyTargetsByPr: PrV2ActiveReplyTargetsByPr;
  /** Reads one thread draft body within a PR scope. */
  getReplyDraft: (prKey: string, threadDraftKey: string) => string | undefined;
  /** Reads the active reply target for one thread within a PR scope. */
  getActiveReplyTarget: (prKey: string, threadDraftKey: string) => string | undefined;
  /** Upserts one thread draft; blank/whitespace values clear the draft instead. */
  setReplyDraft: (prKey: string, threadDraftKey: string, value: string) => void;
  /** Upserts one active reply target; null/blank values clear the target instead. */
  setActiveReplyTarget: (prKey: string, threadDraftKey: string, commentId: string | null) => void;
  /** Removes drafts for stale thread keys after thread list refreshes. */
  pruneReplyDraftsToExistingKeys: (prKey: string, threadDraftKeys: string[]) => void;
  /** Removes active reply targets for stale thread keys after thread list refreshes. */
  pruneActiveReplyTargetsToExistingKeys: (prKey: string, threadDraftKeys: string[]) => void;
  /** Clears all reply drafts under one PR namespace. */
  clearReplyDraftsForPr: (prKey: string) => void;
  /** Clears all active reply targets under one PR namespace. */
  clearActiveReplyTargetsForPr: (prKey: string) => void;
};

/** Returns the stable per-PR namespace used by the reply draft store. */
export function getPrV2ReplyDraftStoreKey(owner: string, repo: string, number: string): string {
  return `${owner}/${repo}/${number}`;
}

/**
 * Removes one thread reply draft from a PR-scoped draft map.
 *
 * If the removed draft was the last one for the PR, this also removes the PR key
 * so state stays compact and avoids lingering empty objects.
 */
function withClearedReplyDraftForThread(
  replyDraftsByPr: PrV2ReplyDraftsByPr,
  prKey: string,
  threadDraftKey: string,
): PrV2ReplyDraftsByPr {
  const draftsByThread = replyDraftsByPr[prKey];
  if (!draftsByThread || !(threadDraftKey in draftsByThread)) {
    return replyDraftsByPr;
  }

  const nextDraftsByThread = { ...draftsByThread };
  delete nextDraftsByThread[threadDraftKey];

  if (Object.keys(nextDraftsByThread).length === 0) {
    const nextReplyDraftsByPr = { ...replyDraftsByPr };
    delete nextReplyDraftsByPr[prKey];
    return nextReplyDraftsByPr;
  }

  return {
    ...replyDraftsByPr,
    [prKey]: nextDraftsByThread,
  };
}

/**
 * Writes/updates one reply draft for a thread within a PR-scoped draft map.
 *
 * Returns the original object when the value is unchanged to preserve
 * referential equality and avoid unnecessary subscriber re-renders.
 */
function withUpdatedReplyDraftForThread(
  replyDraftsByPr: PrV2ReplyDraftsByPr,
  prKey: string,
  threadDraftKey: string,
  value: string,
): PrV2ReplyDraftsByPr {
  const draftsByThread = replyDraftsByPr[prKey] ?? null;
  if (draftsByThread?.[threadDraftKey] === value) {
    return replyDraftsByPr;
  }

  return {
    ...replyDraftsByPr,
    [prKey]: {
      ...(draftsByThread ?? {}),
      [threadDraftKey]: value,
    },
  };
}

/**
 * Removes one active reply target from a PR-scoped target map.
 *
 * If the removed target was the last one for the PR, this also removes the PR
 * key so state stays compact and avoids lingering empty objects.
 */
function withClearedActiveReplyTargetForThread(
  activeReplyTargetsByPr: PrV2ActiveReplyTargetsByPr,
  prKey: string,
  threadDraftKey: string,
): PrV2ActiveReplyTargetsByPr {
  const targetsByThread = activeReplyTargetsByPr[prKey];
  if (!targetsByThread || !(threadDraftKey in targetsByThread)) {
    return activeReplyTargetsByPr;
  }

  const nextTargetsByThread = { ...targetsByThread };
  delete nextTargetsByThread[threadDraftKey];

  if (Object.keys(nextTargetsByThread).length === 0) {
    const nextActiveReplyTargetsByPr = { ...activeReplyTargetsByPr };
    delete nextActiveReplyTargetsByPr[prKey];
    return nextActiveReplyTargetsByPr;
  }

  return {
    ...activeReplyTargetsByPr,
    [prKey]: nextTargetsByThread,
  };
}

/**
 * Writes/updates one active reply target for a thread within a PR-scoped target map.
 *
 * Returns the original object when the value is unchanged to preserve
 * referential equality and avoid unnecessary subscriber re-renders.
 */
function withUpdatedActiveReplyTargetForThread(
  activeReplyTargetsByPr: PrV2ActiveReplyTargetsByPr,
  prKey: string,
  threadDraftKey: string,
  commentId: string,
): PrV2ActiveReplyTargetsByPr {
  const targetsByThread = activeReplyTargetsByPr[prKey] ?? null;
  if (targetsByThread?.[threadDraftKey] === commentId) {
    return activeReplyTargetsByPr;
  }

  return {
    ...activeReplyTargetsByPr,
    [prKey]: {
      ...(targetsByThread ?? {}),
      [threadDraftKey]: commentId,
    },
  };
}

/**
 * PR V2 reply draft store, persisted in session storage.
 * Session scope avoids cross-browser-session draft bleed while surviving route transitions.
 */
export const usePrV2ReplyDraftStore = create<PrV2ReplyDraftStore>()(
  persist(
    (set, get) => ({
      replyDraftsByPr: {},
      activeReplyTargetsByPr: {},
      getReplyDraft: (prKey, threadDraftKey) => get().replyDraftsByPr[prKey]?.[threadDraftKey],
      getActiveReplyTarget: (prKey, threadDraftKey) =>
        get().activeReplyTargetsByPr[prKey]?.[threadDraftKey],
      setReplyDraft: (prKey, threadDraftKey, value) => {
        set((state) => {
          // Treat blank body as delete so we don't persist empty draft entries.
          const nextReplyDraftsByPr = value.trim()
            ? withUpdatedReplyDraftForThread(state.replyDraftsByPr, prKey, threadDraftKey, value)
            : withClearedReplyDraftForThread(state.replyDraftsByPr, prKey, threadDraftKey);

          if (nextReplyDraftsByPr === state.replyDraftsByPr) {
            return state;
          }

          return {
            replyDraftsByPr: nextReplyDraftsByPr,
          };
        });
      },
      setActiveReplyTarget: (prKey, threadDraftKey, commentId) => {
        set((state) => {
          const nextActiveReplyTargetsByPr =
            typeof commentId === 'string' && commentId.trim().length > 0
              ? withUpdatedActiveReplyTargetForThread(
                  state.activeReplyTargetsByPr,
                  prKey,
                  threadDraftKey,
                  commentId,
                )
              : withClearedActiveReplyTargetForThread(
                  state.activeReplyTargetsByPr,
                  prKey,
                  threadDraftKey,
                );

          if (nextActiveReplyTargetsByPr === state.activeReplyTargetsByPr) {
            return state;
          }

          return {
            activeReplyTargetsByPr: nextActiveReplyTargetsByPr,
          };
        });
      },
      pruneReplyDraftsToExistingKeys: (prKey, threadDraftKeys) => {
        set((state) => {
          const draftsByThread = state.replyDraftsByPr[prKey];
          if (!draftsByThread) {
            return state;
          }

          const existingThreadDraftKeys = new Set(threadDraftKeys);
          let hasChanges = false;
          const nextDraftsByThread: PrV2ReplyDraftsByThread = {};

          Object.entries(draftsByThread).forEach(([threadDraftKey, body]) => {
            if (!existingThreadDraftKeys.has(threadDraftKey) || !body.trim()) {
              hasChanges = true;
              return;
            }

            nextDraftsByThread[threadDraftKey] = body;
          });

          if (!hasChanges) {
            return state;
          }

          // Remove the PR key entirely when no drafts remain.
          if (Object.keys(nextDraftsByThread).length === 0) {
            const nextReplyDraftsByPr = { ...state.replyDraftsByPr };
            delete nextReplyDraftsByPr[prKey];
            return {
              replyDraftsByPr: nextReplyDraftsByPr,
            };
          }

          return {
            replyDraftsByPr: {
              ...state.replyDraftsByPr,
              [prKey]: nextDraftsByThread,
            },
          };
        });
      },
      pruneActiveReplyTargetsToExistingKeys: (prKey, threadDraftKeys) => {
        set((state) => {
          const targetsByThread = state.activeReplyTargetsByPr[prKey];
          if (!targetsByThread) {
            return state;
          }

          const existingThreadDraftKeys = new Set(threadDraftKeys);
          let hasChanges = false;
          const nextTargetsByThread: PrV2ActiveReplyTargetsByThread = {};

          Object.entries(targetsByThread).forEach(([threadDraftKey, commentId]) => {
            if (!existingThreadDraftKeys.has(threadDraftKey) || !commentId.trim()) {
              hasChanges = true;
              return;
            }

            nextTargetsByThread[threadDraftKey] = commentId;
          });

          if (!hasChanges) {
            return state;
          }

          // Remove the PR key entirely when no active targets remain.
          if (Object.keys(nextTargetsByThread).length === 0) {
            const nextActiveReplyTargetsByPr = { ...state.activeReplyTargetsByPr };
            delete nextActiveReplyTargetsByPr[prKey];
            return {
              activeReplyTargetsByPr: nextActiveReplyTargetsByPr,
            };
          }

          return {
            activeReplyTargetsByPr: {
              ...state.activeReplyTargetsByPr,
              [prKey]: nextTargetsByThread,
            },
          };
        });
      },
      clearReplyDraftsForPr: (prKey) => {
        set((state) => {
          if (!(prKey in state.replyDraftsByPr)) {
            return state;
          }

          const nextReplyDraftsByPr = { ...state.replyDraftsByPr };
          delete nextReplyDraftsByPr[prKey];
          return {
            replyDraftsByPr: nextReplyDraftsByPr,
          };
        });
      },
      clearActiveReplyTargetsForPr: (prKey) => {
        set((state) => {
          if (!(prKey in state.activeReplyTargetsByPr)) {
            return state;
          }

          const nextActiveReplyTargetsByPr = { ...state.activeReplyTargetsByPr };
          delete nextActiveReplyTargetsByPr[prKey];
          return {
            activeReplyTargetsByPr: nextActiveReplyTargetsByPr,
          };
        });
      },
    }),
    {
      name: 'pr-v2-reply-draft-store',
      storage: createJSONStorage(() => safeSessionStorage),
      // Persist only draft data; actions/selectors are recreated at runtime.
      partialize: (state) => ({
        replyDraftsByPr: state.replyDraftsByPr,
        activeReplyTargetsByPr: state.activeReplyTargetsByPr,
      }),
    },
  ),
);
